import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { getActiveOrUpcomingClosure, loadClosures } from "@/lib/hours";
import { CLOSURE_ICONS } from "@/lib/closureIcons";

const STORAGE_KEY = "cfm_holiday_dismissed_v1";

// Popup appears ONLY for closures where the dev explicitly enabled it
const pickPopupClosure = () => {
  const c = getActiveOrUpcomingClosure();
  return c && c.show_popup ? c : null;
};

export const HolidayNotice = () => {
  const { t } = useLang();
  const h = t.holiday;
  const [closure, setClosure] = useState(() => pickPopupClosure());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    loadClosures().then(() => setClosure(pickPopupClosure()));
  }, []);

  useEffect(() => {
    if (!closure) return;
    if (sessionStorage.getItem(STORAGE_KEY) === closure.start) return;
    const timer = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(timer);
  }, [closure]);

  const dismiss = () => {
    if (closure) sessionStorage.setItem(STORAGE_KEY, closure.start);
    setOpen(false);
  };

  if (!closure || !h) return null;

  // Format dates as DD/MM
  const fmt = (iso) => {
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
  };
  const range = closure.start === closure.end ? fmt(closure.start) : `${fmt(closure.start)} — ${fmt(closure.end)}`;
  const Icon = CLOSURE_ICONS[closure.icon] || Sun;
  const bodyText = closure.popup_text || h.body;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1000] grid place-items-center px-4 bg-brand-ink/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={dismiss}
          data-testid="holiday-overlay"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-[2rem] bg-brand-cream border border-brand/15 shadow-[0_30px_80px_rgba(134,77,99,0.35)] overflow-hidden"
            data-testid="holiday-notice"
          >
            <button
              type="button"
              onClick={dismiss}
              aria-label={h.dismiss}
              data-testid="holiday-close-btn"
              className="absolute top-3 right-3 z-10 grid place-items-center h-9 w-9 rounded-full bg-white/80 hover:bg-white text-brand transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="bg-gradient-to-br from-brand to-brand-hover text-brand-cream px-7 pt-8 pb-6 text-center">
              <span className="inline-grid place-items-center h-14 w-14 rounded-full bg-brand-cream/15 backdrop-blur-sm mb-3">
                <Icon className="h-7 w-7" />
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">{closure.reason || h.title}</h2>
              <p className="mt-2 text-brand-cream/85 text-sm font-medium tracking-wide uppercase">{h.subtitle}</p>
            </div>

            <div className="px-7 py-6 text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-bamboo-soft border border-bamboo/25 px-4 py-2 text-bamboo-deep font-bold">
                <span className="text-xs uppercase tracking-wide opacity-80">{h.range_label}</span>
                <span className="font-heading text-lg" data-testid="holiday-range">{range}</span>
              </div>
              <p className="mt-4 text-brand-ink/75 leading-relaxed">{bodyText}</p>
              <p className="mt-3 text-sm text-brand-ink/60">{h.thanks}</p>

              <Button
                onClick={dismiss}
                data-testid="holiday-dismiss-btn"
                className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12"
              >
                {h.dismiss}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HolidayNotice;
